import { useCallback, useEffect, useState } from "react";
import { adminApi, fmtDate, fmtDuration } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, Power, PowerOff, ExternalLink, Trash2, Mail, Bell } from "lucide-react";

export default function AdminDashboard({ adminKey }) {
  const [codes, setCodes] = useState([]);
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [form, setForm] = useState({ partner_name: "", notes: "", code: "" });

  const client = adminApi(adminKey);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        client.get("/admin/codes"),
        client.get("/admin/overview"),
        client.get("/admin/alerts/warm-leads"),
      ]);
      setCodes(a.data);
      setOverview(b.data);
      setAlerts(c.data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.partner_name.trim()) { toast.error("Partner name required"); return; }
    setCreating(true);
    try {
      const { data } = await client.post("/admin/codes", form);
      toast.success(`Code created: ${data.code}`);
      setForm({ partner_name: "", notes: "", code: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (c) => {
    try {
      await client.patch(`/admin/codes/${c.code}`, { active: !c.active });
      toast.success(!c.active ? "Code activated" : "Code deactivated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? Analytics will be preserved but the code can no longer be used.`)) return;
    try {
      await client.delete(`/admin/codes/${c.code}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const copy = (c) => {
    navigator.clipboard.writeText(c.code);
    toast.success(`Copied: ${c.code}`);
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await client.post("/admin/alerts/test");
      toast.success(`Test email sent to ${data.sent_to.join(", ")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="px-6 md:px-10 py-10 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <p className="overline mb-2">Dashboard</p>
          <h1 className="font-display text-5xl tracking-tighter">Partner Access & Analytics</h1>
        </div>
        <button
          onClick={sendTest}
          disabled={sendingTest}
          data-testid="send-test-email"
          className="btn-ghost disabled:opacity-50"
        >
          <Mail className="w-4 h-4" />
          {sendingTest ? "Sending…" : "Send test alert email"}
        </button>
      </div>

      {/* Overview stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-foreground/20 border border-foreground/20 mb-10">
          {[
            { k: "Codes", v: overview.codes, sub: `${overview.active_codes} active` },
            { k: "Sessions", v: overview.sessions },
            { k: "Slide views", v: overview.views },
            { k: "Leads", v: overview.leads },
            { k: "Top slide", v: overview.top_slides[0]?.slide_label || "—",
              sub: overview.top_slides[0] ? `${overview.top_slides[0].views} views` : "" },
          ].map((s, i) => (
            <div key={i} className="bg-background p-5">
              <p className="overline mb-2">{s.k}</p>
              <p className="font-mono-data text-3xl tabular">{s.v ?? 0}</p>
              {s.sub && <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Create code */}
      <div className="bg-white border border-foreground p-6 mb-10">
        <p className="overline mb-4">New partner code</p>
        <form onSubmit={create} className="grid md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-4">
            <label className="overline block mb-2">Partner name *</label>
            <input
              value={form.partner_name}
              onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
              data-testid="create-code-partner"
              className="input-sharp"
              placeholder="Acme Lending"
            />
          </div>
          <div className="md:col-span-3">
            <label className="overline block mb-2">Custom code (optional)</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              data-testid="create-code-code"
              className="input-sharp"
              placeholder="Auto-generated"
            />
          </div>
          <div className="md:col-span-3">
            <label className="overline block mb-2">Notes</label>
            <input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="create-code-notes"
              className="input-sharp"
              placeholder="e.g. Intro by Jeff"
            />
          </div>
          <div className="md:col-span-2">
            <button type="submit" disabled={creating} data-testid="create-code-submit"
              className="btn-primary w-full disabled:opacity-50">
              <Plus className="w-4 h-4" /> {creating ? "…" : "Create"}
            </button>
          </div>
        </form>
      </div>

      {/* Codes table */}
      <div className="border border-foreground">
        <div className="flex items-center justify-between px-6 py-4 border-b border-foreground">
          <span className="overline">Active partner codes</span>
          <button onClick={load} className="text-xs uppercase tracking-brand hover:text-primary">
            Refresh
          </button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground font-display text-xl">
            No codes yet — create one above to start tracking.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="codes-table">
              <thead>
                <tr className="bg-muted text-left">
                  <th className="px-5 py-3 overline">Code</th>
                  <th className="px-5 py-3 overline">Partner</th>
                  <th className="px-5 py-3 overline text-right">Sessions</th>
                  <th className="px-5 py-3 overline text-right">Views</th>
                  <th className="px-5 py-3 overline text-right">Leads</th>
                  <th className="px-5 py-3 overline">Last seen</th>
                  <th className="px-5 py-3 overline">Status</th>
                  <th className="px-5 py-3 overline text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} className="border-t border-border hover:bg-muted/50"
                      data-testid={`code-row-${c.code}`}>
                    <td className="px-5 py-3 font-mono-data">
                      <button onClick={() => copy(c)} className="inline-flex items-center gap-2 hover:text-primary">
                        {c.code}<Copy className="w-3 h-3 opacity-60" />
                      </button>
                    </td>
                    <td className="px-5 py-3 font-display text-lg">{c.partner_name}</td>
                    <td className="px-5 py-3 text-right font-mono-data">{c.sessions ?? 0}</td>
                    <td className="px-5 py-3 text-right font-mono-data">{c.views ?? 0}</td>
                    <td className="px-5 py-3 text-right font-mono-data">{c.leads ?? 0}</td>
                    <td className="px-5 py-3 font-mono-data text-xs text-muted-foreground">
                      {fmtDate(c.last_seen_at)}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-brand font-bold ${c.active ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`}>
                        {c.active ? "Active" : "Paused"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <Link to={`/admin/codes/${c.code}`}
                          data-testid={`view-code-${c.code}`}
                          className="inline-flex items-center gap-1 hover:text-primary text-xs uppercase tracking-brand">
                          Details <ExternalLink className="w-3 h-3" />
                        </Link>
                        <button onClick={() => toggle(c)} title={c.active ? "Pause" : "Activate"}
                          data-testid={`toggle-code-${c.code}`}
                          className="hover:text-primary">
                          {c.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </button>
                        <button onClick={() => remove(c)} title="Delete"
                          data-testid={`delete-code-${c.code}`}
                          className="hover:text-primary">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Warm-lead alerts log */}
      <div className="border border-foreground mt-10" data-testid="alerts-log">
        <div className="flex items-center justify-between px-6 py-4 border-b border-foreground">
          <div className="flex items-center gap-3">
            <Bell className="w-4 h-4 text-primary" strokeWidth={1.75} />
            <span className="overline">Warm-lead alerts sent</span>
          </div>
          <span className="text-[11px] uppercase tracking-brand text-muted-foreground">
            Fired when a partner idles on Next Steps ≥ 90s
          </span>
        </div>
        {alerts.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground font-display text-xl">
            No alerts yet. Walk through the deck (or leave the Next Steps slide open for ~90 seconds) to trigger one.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {alerts.map((a) => (
              <li key={a.alert_id} className="px-6 py-4 flex items-start justify-between gap-6">
                <div>
                  <p className="font-display text-xl">
                    {a.partner_name || "Shared-link visitor"}
                    {a.code && <span className="text-muted-foreground text-sm font-mono-data ml-2">({a.code})</span>}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{a.subject}</p>
                  {a.context?.location_label && (
                    <p className="text-xs text-foreground mt-1 font-mono-data">
                      📍 {a.context.location_label}
                    </p>
                  )}
                  <p className="font-mono-data text-xs text-muted-foreground mt-1">
                    to {a.sent_to?.join(", ")}
                  </p>
                </div>
                <span className="font-mono-data text-xs text-muted-foreground whitespace-nowrap">
                  {fmtDate(a.sent_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
